import React from "react";

interface TableProps {
  children: React.ReactNode;
  className?: string;
  compact?: boolean;
}

export const Table: React.FC<TableProps> = ({
  children,
  className = "",
  compact = false,
}) => {
  return (
    <div className="overflow-x-auto">
      <table className={`w-full border-collapse ${className}`}>
        {children}
      </table>
    </div>
  );
};

interface TableHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const TableHeader: React.FC<TableHeaderProps> = ({
  children,
  className = "",
}) => {
  return (
    <thead className={`bg-gray-50 ${className}`}>
      {children}
    </thead>
  );
};

interface TableBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const TableBody: React.FC<TableBodyProps> = ({
  children,
  className = "",
}) => {
  return (
    <tbody className={`divide-y divide-gray-200 ${className}`}>
      {children}
    </tbody>
  );
};

interface TableRowProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  hover?: boolean;
}

export const TableRow: React.FC<TableRowProps> = ({
  children,
  className = "",
  onClick,
  hover = true,
}) => {
  const hoverClass = hover ? "hover:bg-gray-50" : "";
  const clickClass = onClick ? "cursor-pointer" : "";

  return (
    <tr
      className={`${hoverClass} ${clickClass} ${className}`}
      onClick={onClick}
    >
      {children}
    </tr>
  );
};

interface TableCellProps {
  children: React.ReactNode;
  className?: string;
  align?: "left" | "center" | "right";
  header?: boolean;
}

export const TableCell: React.FC<TableCellProps> = ({
  children,
  className = "",
  align = "left",
  header = false,
}) => {
  const alignClass = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  }[align];

  const Component = header ? "th" : "td";

  if (header) {
    return (
      <Component
        className={`px-4 py-3 text-xs font-semibold text-gray-700 uppercase tracking-wider ${alignClass} ${className}`}
      >
        {children}
      </Component>
    );
  }

  return (
    <Component
      className={`px-4 py-3 text-sm text-gray-900 ${alignClass} ${className}`}
    >
      {children}
    </Component>
  );
};
